/**
 * Tracks design-studio features that leave no trace in the saved template — a pasted dimension is
 * identical to a typed one — so adoption can be measured per template.
 *
 * Travels as a request header on the save it belongs to, attached by the HTTP interceptors, so
 * neither the save payload nor its DTO has to carry telemetry. This enum is the only registry: the
 * server just validates the `ds.*` key format (TemplateFeatureUsageBL), so new keys need no backend change.
 */
// Not `const`: the AngularJS controllers are plain JS and need the runtime object
export enum TrackedFeature {
    TransformPasteDimensions = 'ds.transform.paste-dimensions',
    TransformPastePosition = 'ds.transform.paste-position',

    ColorsPasteFill = 'ds.colors.paste-fill',
    ColorsPasteStroke = 'ds.colors.paste-stroke',
    ColorsPasteVector = 'ds.colors.paste-vector',
    ColorsPasteShadow = 'ds.colors.paste-shadow',
    ColorsPasteImageOutline = 'ds.colors.paste-image-outline',

    ToolbarDuplicate = 'ds.toolbar.duplicate',
    ToolbarDelete = 'ds.toolbar.delete',
    ToolbarFitToCanvas = 'ds.toolbar.fit-to-canvas',
    ToolbarAlignTop = 'ds.toolbar.align-top',
    ToolbarAlignMiddle = 'ds.toolbar.align-middle',
    ToolbarAlignBottom = 'ds.toolbar.align-bottom',
    ToolbarAlignLeft = 'ds.toolbar.align-left',
    ToolbarAlignCenter = 'ds.toolbar.align-center',
    ToolbarAlignRight = 'ds.toolbar.align-right',

    FlipHorizontal = 'ds.flip.horizontal',
    FlipVertical = 'ds.flip.vertical',

    ArrangeBringToFront = 'ds.arrange.bring-to-front',
    ArrangeBringForward = 'ds.arrange.bring-forward',
    ArrangeSendBackward = 'ds.arrange.send-backward',
    ArrangeSendToBack = 'ds.arrange.send-to-back',

    FitWidth = 'ds.fit.width',
    FitHeight = 'ds.fit.height',

    ImportCanva = 'ds.import.canva',
    ImportPsd = 'ds.import.psd',

    PreviewReplaceBackground = 'ds.preview.replace-background',
    PreviewResetWhole = 'ds.preview.reset-whole',
    PreviewResetElements = 'ds.preview.reset-elements'
}

export const FEATURE_USAGE_HEADER = 'X-Customily-Feature-Usage';

// Scoped to the template open in the editor; ProductService.clearProduct() resets it.
const pending = new Map<string, number>();

export const recordFeatureUsage = (feature: TrackedFeature | string): void => {
    pending.set(feature, (pending.get(feature) ?? 0) + 1);
};

/**
 * Serializes as `feature:count,feature:count` and clears, so five saves don't report five uses.
 * Returns null when nothing accumulated, so callers can omit the header entirely. Counts lost to a
 * failed request are acceptable: the row's existence, not UseCount, is what the reporting needs.
 */
export const drainFeatureUsageHeader = (): string | null => {
    if (pending.size === 0) return null;
    const header = Array.from(pending, ([feature, count]) => `${feature}:${count}`).join(',');
    pending.clear();
    return header;
};

export const resetFeatureUsage = (): void => {
    pending.clear();
};

export const isSaveProductRequest = (url?: string): boolean => !!url && url.includes('Product/SaveProduct');
